/**
 * Карточки для шеринга (C5): запись о рождении и свидетельство о смерти,
 * canvas 1080×1350 → PNG. Рисуется в палитре светлой темы дизайн-системы
 * (бумага, чернила, линейки; на свидетельстве акцента нет — правило смерти),
 * шрифты те же, что в интерфейсе, с теми же запасными.
 *
 * Без персональных данных: на карточке только имя персонажа, страна, годы,
 * факты из движка, последние слова и подпись «Один из восьми миллиардов».
 * Ни id, ни seed не попадают ни в текст, ни в метаданные.
 *
 * Тексты карточки собираются отдельно (`birthLines`, `certificateLines`),
 * чтобы их можно было проверить без canvas.
 */
import type { Dataset } from '@life/data';
import type { Life } from '@life/engine';
import { t, yearsWord } from '@life/i18n';
import { birthCountry, certificateFacts, expectedYears } from '../lib/certificate';

export const CARD_WIDTH = 1080;
export const CARD_HEIGHT = 1350;

/** Светлая тема DESIGN-SYSTEM.md. */
const PAPER = '#f4efe6';
const PAPER_2 = '#ebe4d8';
const PAPER_3 = '#e0d7c8';
const INK = '#1b1a17';
const INK_2 = '#4a4740';
const INK_3 = '#7b766c';
const RULE = '#c9bfae';

const FD = "'Piazzolla', Georgia, serif";
const FB = "'Golos Text', system-ui, sans-serif";
const FM = "'JetBrains Mono', ui-monospace, monospace";

const PAD = 72;
const STRIP_CELLS = 80;
const STRIP_COLS = 40;
const STRIP_GAP = 6;

export interface CertificateOpts {
  lastWords: string;
  keyYears: number[];
}

/** Строки карточки рождения, сверху вниз. */
export function birthLines(life: Life, ds: Dataset): string[] {
  const e0 = expectedYears(ds, life.iso3, life);
  return [
    t('ui.share.birth.eyebrow'),
    life.name,
    t('ui.share.birth.place', { country: birthCountry(life, ds), year: life.birthYear }),
    `${t(`ui.birth.sex.${life.sex}`)} · ${t(life.urban ? 'ui.birth.urban' : 'ui.birth.rural')}`,
    t('ui.birth.family', { decile: t(`ui.birth.decile.${life.familyDecile}`) }),
    t('ui.share.birth.lifeExp', { n: e0, years: yearsWord(e0) }),
    t('ui.share.signature'),
  ];
}

/** Строки свидетельства, сверху вниз; пары «dt: dd» склеены двоеточием. */
export function certificateLines(life: Life, ds: Dataset, opts: CertificateOpts): string[] {
  const f = certificateFacts(life, ds, false);
  const lines = [
    t('ui.share.death.eyebrow'),
    f.name,
    f.span,
    `${t('ui.death.lived')}: ${f.lived}`,
    `${t('ui.death.cause')}: ${f.cause}`,
    `${t('ui.death.education')}: ${f.education}`,
    `${t('ui.death.work')}: ${f.work}`,
    `${t('ui.death.family')}: ${f.family}`,
    `${t('ui.death.moves')}: ${f.moves}`,
  ];
  if (opts.keyYears.length > 0) lines.push(t('ui.share.keyYears', { years: opts.keyYears.join(', ') }));
  if (opts.lastWords.trim() !== '') lines.push(`«${opts.lastWords.trim()}»`);
  lines.push(t('ui.share.signature'));
  return lines;
}

function makeCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  if (typeof document === 'undefined') throw new Error('share card: no document');
  const canvas = document.createElement('canvas');
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('share card: canvas 2d context unavailable');
  return { canvas, ctx };
}

function toPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob === null) reject(new Error('share card: toBlob returned null'));
      else resolve(blob);
    }, 'image/png');
  });
}

/** Бумага с едва заметным зерном (точечный паттерн, как в интерфейсе). */
function paintPaper(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
  ctx.fillStyle = 'rgba(27, 26, 23, 0.025)';
  for (let y = 0; y < CARD_HEIGHT; y += 6) {
    for (let x = 0; x < CARD_WIDTH; x += 6) ctx.fillRect(x, y, 1, 1);
  }
}

/** Двойная рамка свидетельства; одинарная — у карточки рождения. */
function paintFrame(ctx: CanvasRenderingContext2D, double: boolean): void {
  ctx.strokeStyle = RULE;
  ctx.lineWidth = 2;
  ctx.strokeRect(24, 24, CARD_WIDTH - 48, CARD_HEIGHT - 48);
  if (double) ctx.strokeRect(40, 40, CARD_WIDTH - 80, CARD_HEIGHT - 80);
}

/** Разбивает строку по словам под ширину. */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const out: string[] = [];
  let line = '';
  for (const w of words) {
    const probe = line === '' ? w : `${line} ${w}`;
    if (ctx.measureText(probe).width > maxWidth && line !== '') {
      out.push(line);
      line = w;
    } else {
      line = probe;
    }
  }
  if (line !== '') out.push(line);
  return out;
}

interface TextStyle {
  font: string;
  color: string;
  align?: CanvasTextAlign;
  lineHeight: number;
  letterSpacing?: string;
}

function drawText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, style: TextStyle, maxWidth: number): number {
  ctx.font = style.font;
  ctx.fillStyle = style.color;
  ctx.textAlign = style.align ?? 'left';
  ctx.textBaseline = 'alphabetic';
  if ('letterSpacing' in ctx) (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = style.letterSpacing ?? '0px';
  let cursor = y;
  for (const line of wrap(ctx, text, maxWidth)) {
    ctx.fillText(line, x, cursor);
    cursor += style.lineHeight;
  }
  return cursor;
}

/** Карта жизни 40 × 2: прожитые — чернила, остальные — пунктир; важные годы — точка бумаги. */
function paintStrip(ctx: CanvasRenderingContext2D, lived: number, y: number, marked: ReadonlySet<number>): number {
  const width = CARD_WIDTH - PAD * 2;
  const cell = (width - STRIP_GAP * (STRIP_COLS - 1)) / STRIP_COLS;
  for (let i = 0; i < STRIP_CELLS; i++) {
    const col = i % STRIP_COLS;
    const row = Math.floor(i / STRIP_COLS);
    const x = PAD + col * (cell + STRIP_GAP);
    const cy = y + row * (cell + STRIP_GAP);
    if (i < lived) {
      ctx.fillStyle = INK;
      ctx.fillRect(x, cy, cell, cell);
      if (marked.has(i)) {
        ctx.fillStyle = PAPER;
        const d = cell * 0.3;
        ctx.fillRect(x + (cell - d) / 2, cy + (cell - d) / 2, d, d);
      }
    } else {
      ctx.strokeStyle = RULE;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(x + 0.5, cy + 0.5, cell - 1, cell - 1);
      ctx.setLineDash([]);
    }
  }
  return y + cell * 2 + STRIP_GAP;
}

function paintRule(ctx: CanvasRenderingContext2D, y: number): void {
  ctx.fillStyle = RULE;
  ctx.fillRect(PAD, y, CARD_WIDTH - PAD * 2, 1);
}

function paintSignature(ctx: CanvasRenderingContext2D): void {
  paintRule(ctx, CARD_HEIGHT - 128);
  drawText(
    ctx,
    t('ui.share.signature'),
    CARD_WIDTH / 2,
    CARD_HEIGHT - 80,
    { font: `500 22px ${FB}`, color: INK_3, align: 'center', lineHeight: 30, letterSpacing: '3px' },
    CARD_WIDTH - PAD * 2,
  );
}

const EYEBROW: TextStyle = { font: `500 22px ${FB}`, color: INK_3, align: 'center', lineHeight: 30, letterSpacing: '3px' };
const NAME: TextStyle = { font: `300 88px ${FD}`, color: INK, align: 'center', lineHeight: 96, letterSpacing: '-2px' };
const SUB: TextStyle = { font: `400 30px ${FB}`, color: INK_3, align: 'center', lineHeight: 42 };
const FACT: TextStyle = { font: `400 32px ${FB}`, color: INK_2, align: 'center', lineHeight: 54 };
const KEY: TextStyle = { font: `400 26px ${FB}`, color: INK_3, lineHeight: 44 };
const VAL: TextStyle = { font: `400 26px ${FM}`, color: INK, lineHeight: 44 };
const QUOTE: TextStyle = { font: `italic 300 40px ${FD}`, color: INK, align: 'center', lineHeight: 52 };

/** Карточка рождения: eyebrow, имя, факты, пустая карта жизни, подпись. */
export async function renderBirthCard(life: Life, ds: Dataset): Promise<Blob> {
  const { canvas, ctx } = makeCanvas();
  const lines = birthLines(life, ds);
  const width = CARD_WIDTH - PAD * 2;
  paintPaper(ctx);
  paintFrame(ctx, false);

  let y = 200;
  y = drawText(ctx, lines[0]!.toUpperCase(), CARD_WIDTH / 2, y, EYEBROW, width) + 40;
  y = drawText(ctx, lines[1]!, CARD_WIDTH / 2, y + 60, NAME, width) + 24;
  for (const fact of lines.slice(2, -1)) y = drawText(ctx, fact, CARD_WIDTH / 2, y + 10, FACT, width);

  ctx.fillStyle = PAPER_2;
  ctx.fillRect(PAD, 880, width, 110);
  paintStrip(ctx, 0, 900, new Set());
  paintSignature(ctx);
  return toPng(canvas);
}

/** Свидетельство: двойная рамка, имя, таблица фактов, карта жизни, цитата, подпись. */
export async function renderCertificate(life: Life, ds: Dataset, opts: CertificateOpts): Promise<Blob> {
  const { canvas, ctx } = makeCanvas();
  const f = certificateFacts(life, ds, false);
  const width = CARD_WIDTH - PAD * 2;
  paintPaper(ctx);
  paintFrame(ctx, true);

  let y = 170;
  y = drawText(ctx, t('ui.share.death.eyebrow').toUpperCase(), CARD_WIDTH / 2, y, EYEBROW, width) + 30;
  y = drawText(ctx, f.name, CARD_WIDTH / 2, y + 60, NAME, width);
  y = drawText(ctx, f.span, CARD_WIDTH / 2, y, SUB, width) + 30;
  paintRule(ctx, y);
  y += 56;

  const rows: Array<[string, string]> = [
    [t('ui.death.lived'), f.lived],
    [t('ui.death.cause'), f.cause],
    [t('ui.death.education'), f.education],
    [t('ui.death.work'), f.work],
    [t('ui.death.family'), f.family],
    [t('ui.death.moves'), f.moves],
  ];
  const colW = (width - 40) / 2;
  rows.forEach(([k, v], i) => {
    const x = PAD + (i % 2) * (colW + 40);
    const ry = y + Math.floor(i / 2) * 100;
    drawText(ctx, k, x, ry, KEY, colW);
    drawText(ctx, v, x, ry + 40, VAL, colW);
  });
  y += 3 * 100 + 10;

  ctx.fillStyle = PAPER_3;
  ctx.fillRect(PAD, y - 16, width, 1);
  // Умерший: прожито ровно `age` клеток, без охры (правило смерти).
  y = paintStrip(ctx, Math.min(STRIP_CELLS, life.age), y + 16, new Set(opts.keyYears)) + 60;

  const words = opts.lastWords.trim();
  if (words !== '') y = drawText(ctx, `«${words}»`, CARD_WIDTH / 2, y + 20, QUOTE, width - 80);
  if (opts.keyYears.length > 0) {
    drawText(ctx, t('ui.share.keyYears', { years: opts.keyYears.join(', ') }), CARD_WIDTH / 2, y + 30, SUB, width);
  }
  paintSignature(ctx);
  return toPng(canvas);
}

/** Скачивание через временную ссылку `<a download>`. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
